<template>
	<div class="stats-panel d-flex flex-column h-100 overflow-hidden">
		<!-- Controls -->
		<div class="stats-controls d-flex flex-wrap align-items-center px-2 pt-2" style="gap: .35rem .6rem">
			<!-- Metric filters (glyph shows the line style used for that metric) -->
			<div class="d-flex align-items-center" style="gap: .5rem">
				<b-form-checkbox v-for="m in metricDefs" :key="m.id" v-model="metricOn[m.id]" size="sm">
					<svg class="stats-glyph" viewBox="0 0 20 8"><line x1="0" y1="4" x2="20" y2="4" :stroke-dasharray="dashArray(m.dash)"></line></svg>{{ m.label }}
				</b-form-checkbox>
			</div>

			<span class="stats-sep"></span>

			<!-- Group mode -->
			<b-button-group size="sm">
				<b-button :variant="groupMode==='channel' ? 'primary' : 'outline-secondary'" @click="setGroup('channel')">Per channel</b-button>
				<b-button :variant="groupMode==='total' ? 'primary' : 'outline-secondary'" @click="setGroup('total')">Total</b-button>
			</b-button-group>

			<span class="stats-sep"></span>

			<!-- Time range / zoom -->
			<b-button-group size="sm">
				<b-button v-for="r in ranges" :key="r.label" :variant="rangeState===r.mins ? 'primary' : 'outline-secondary'" @click="onRangeClick(r.mins)">{{ r.label }}</b-button>
			</b-button-group>
			<small v-if="rangeState==='custom'" class="text-muted">custom zoom</small>
		</div>

		<!-- Channel filter + legend: inline toggle chips (each chip's dot = that channel's line colour) -->
		<div v-if="channelList.length" class="stats-legend d-flex flex-wrap align-items-center px-2 pt-1" style="gap: .25rem .5rem">
			<button
				v-for="c in channelList"
				:key="c.key"
				type="button"
				class="stats-chip"
				:class="{ off: !selected[c.key] }"
				:title="selected[c.key] ? 'Click to hide' : 'Click to show'"
				@click="toggleChannel(c.key)"
			>
				<img v-if="c.serviceIconUrl" :src="c.serviceIconUrl" class="stats-chip-img" :title="c.serviceName" alt="">
				{{ c.title }}
				<span class="stats-dot stats-dot-after" :style="{ background: colorFor(c.key) }"></span>
			</button>
			<a href="#" class="small text-muted ml-1" @click.prevent="allChannels(true)">all</a>
			<a href="#" class="small text-muted" @click.prevent="allChannels(false)">none</a>

			<!-- Metric colour key (total mode only; per-channel line styles are already shown by the metric checkboxes) -->
			<template v-if="groupMode==='total' && legendMetricsTotal.length">
				<span class="stats-sep"></span>
				<span v-for="m in legendMetricsTotal" :key="m.label" class="stats-lg"><span class="stats-dot" :style="{ background: m.color }"></span>{{ m.label }}</span>
			</template>
		</div>

		<!-- Chart / states -->
		<div class="stats-chart-wrap flex-fill position-relative">
			<div v-show="ready && hasData" ref="chart" class="stats-chart"></div>
			<div v-if="loading" class="stats-overlay text-muted"><b-spinner small class="mr-2"></b-spinner>Loading…</div>
			<div v-else-if="error" class="stats-overlay text-danger px-3 text-center">{{ error.error || error.toString() }}</div>
			<div v-else-if="!hasData" class="stats-overlay text-muted px-3 text-center">
				No stats yet. Data appears once a channel goes live and starts receiving viewers or chat.
			</div>
		</div>
	</div>
</template>

<script>
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import FetchPoller from '../FetchPoller';

const POLL_MS = 60 * 1000;
const WINDOW_MS = 12 * 60 * 60 * 1000;
const BUCKET_MS = 60 * 1000; // align samples to the minute

// Per-channel line colors (also reused per-metric in total mode).
const PALETTE = ['#4e9cff', '#ff6b6b', '#39d98a', '#ffb020', '#b980ff', '#00c2d1', '#f25f9c', '#8bc34a', '#ff8f4d', '#7986cb'];
// Metric definitions. Line STYLE encodes the metric (consistent in both group modes);
// `scale` picks the y axis ('y' left for counts, 'c' right for chat rate).
const METRIC_DEFS = [
	{ id: 'viewers', key: 'v', label: 'Viewers',   scale: 'y', dash: null },
	{ id: 'views',   key: 'w', label: 'Views',     scale: 'y', dash: [8, 4] },
	{ id: 'chats',   key: 'c', label: 'Chats/min', scale: 'c', dash: [2, 3] },
];
const AXIS_COLOR = 'rgba(200,200,220,0.75)';
const GRID_COLOR = 'rgba(160,160,190,0.14)';

// Compact axis tick labels: 1000 -> "1k", 1500 -> "1.5k", 2_000_000 -> "2M".
function fmtAxisNum(v) {
	const abs = Math.abs(v);
	if(abs >= 1e6) return (Math.round(v / 1e5) / 10) + 'M';
	if(abs >= 1e3) return (Math.round(v / 1e2) / 10) + 'k';
	return String(v);
}

export default {
	name: 'StatsView',
	data() {
		return {
			loading: true,
			error: null,
			ready: false,
			metricDefs: METRIC_DEFS,
			metricOn: { viewers: true, views: false, chats: true },
			groupMode: 'channel',
			channelList: [], // [{ key, title, serviceName, serviceIconUrl }]
			selected: {},    // key -> bool
			rangeState: 'full', // 'full' | minutes | 'custom'
			ranges: [
				{ mins: 15, label: '15m' },
				{ mins: 60, label: '1h' },
				{ mins: 360, label: '6h' },
				{ mins: 720, label: '12h' },
				{ mins: null, label: 'Full' },
			],
		};
	},
	computed: {
		selectedCount() { return this.channelList.filter(c => this.selected[c.key]).length; },
		hasData() { return this.channelList.some(c => (this.samplesByKey[c.key] || []).length); },
		enabledMetricList() { return METRIC_DEFS.filter(m => this.metricOn[m.id]); },
		legendMetricsTotal() {
			if(this.groupMode !== 'total') return [];
			return this.enabledMetricList.map((m, i) => ({ label: m.label, color: PALETTE[i % PALETTE.length], dash: m.dash }));
		},
	},
	watch: {
		metricOn: { deep: true, handler() { this.render(); } },
		selected: { deep: true, handler() { this.render(); } },
	},
	created() {
		// Heavy sample arrays are kept off the reactive system (raw JS), like the widget classes.
		this.samplesByKey = {};
		this.lastTs = 0;
		this.uplot = null;
		this.sig = null;
		this._programmaticScale = false;
	},
	async mounted() {
		this._resizeObserver = new ResizeObserver(() => this.resize());
		try {
			await this.load();
			this.ready = true;
			this.$nextTick(() => this.render());
			this.poller = new FetchPoller(() => this.poll(), POLL_MS);
			this.poller.start();
		} catch(e) {
			this.error = e;
		} finally {
			this.loading = false;
		}
	},
	beforeDestroy() {
		this.poller?.stop();
		this._resizeObserver?.disconnect();
		if(this.uplot) { this.uplot.destroy(); this.uplot = null; }
	},
	methods: {
		// --- data loading ---
		async load() { this.ingest(await this.$api.get('stats'), true); },
		async poll() {
			const res = await this.$api.get('stats', this.lastTs ? { since: this.lastTs } : undefined);
			this.ingest(res, false);
			this.render();
		},
		ingest(res, replace) {
			if(replace) { this.samplesByKey = {}; this.lastTs = 0; }
			const cutoff = Date.now() - WINDOW_MS;
			for(const ch of res.channels || []) {
				const key = `${ch.serviceId}:${ch.channelToken}`;
				if(!this.channelList.some(c => c.key === key)) {
					this.channelList.push({ key, title: ch.title, serviceName: ch.serviceName, serviceIconUrl: ch.serviceIconUrl });
					this.$set(this.selected, key, true);
				}
				let arr = this.samplesByKey[key] || (this.samplesByKey[key] = []);
				for(const s of ch.samples) {
					arr.push(s);
					if(s.t > this.lastTs) this.lastTs = s.t;
				}
				if(arr.length && arr[0].t < cutoff) this.samplesByKey[key] = arr.filter(s => s.t >= cutoff);
			}
		},

		// --- controls ---
		enabledMetrics() { return this.enabledMetricList; },
		setGroup(mode) { this.groupMode = mode; this.render(); },
		allChannels(on) { for(const c of this.channelList) this.$set(this.selected, c.key, on); },
		toggleChannel(key) { this.$set(this.selected, key, !this.selected[key]); },
		// Stable per-channel colour, keyed to the channel's position in the full list (not the filtered subset)
		// so a channel keeps the same colour regardless of what else is toggled on.
		colorFor(key) { return PALETTE[Math.max(0, this.channelList.findIndex(c => c.key === key)) % PALETTE.length]; },
		dashArray(dash) { return dash ? dash.join(',') : ''; },

		// --- chart data assembly ---
		assemble() {
			const selectedKeys = this.channelList.filter(c => this.selected[c.key]).map(c => c.key);
			const metrics = this.enabledMetricList;

			const buckets = new Map(); // bucketSec -> key -> { v, w, c }
			for(const key of selectedKeys) {
				for(const s of (this.samplesByKey[key] || [])) {
					const bsec = Math.floor(s.t / BUCKET_MS) * (BUCKET_MS / 1000);
					let row = buckets.get(bsec);
					if(!row) { row = new Map(); buckets.set(bsec, row); }
					const prev = row.get(key) || { v: null, w: null, c: 0 };
					row.set(key, {
						v: s.v != null ? s.v : prev.v, // gauges: last value in bucket
						w: s.w != null ? s.w : prev.w,
						c: (prev.c || 0) + (s.c != null ? s.c : 0), // counter: sum in bucket
					});
				}
			}
			const xs = [...buckets.keys()].sort((a, b) => a - b);
			const cols = [];
			const series = [{}]; // series[0] = x

			if(this.groupMode === 'total') {
				metrics.forEach((m, mi) => {
					const carry = {};
					const col = xs.map(bsec => {
						const row = buckets.get(bsec);
						let sum = null;
						for(const key of selectedKeys) {
							const cell = row.get(key);
							if(m.key === 'c') {
								sum = (sum == null ? 0 : sum) + (cell ? cell.c : 0);
							} else {
								if(cell && cell[m.key] != null) carry[key] = cell[m.key];
								if(carry[key] != null) sum = (sum == null ? 0 : sum) + carry[key];
							}
						}
						return sum;
					});
					cols.push(col);
					series.push({ label: m.label, stroke: PALETTE[mi % PALETTE.length], dash: m.dash || undefined, width: 2, scale: m.scale, points: { show: false } });
				});
			} else {
				selectedKeys.forEach(key => {
					const meta = this.channelList.find(c => c.key === key);
					for(const m of metrics) {
						const col = xs.map(bsec => {
							const cell = buckets.get(bsec).get(key);
							return cell && cell[m.key] != null ? cell[m.key] : null;
						});
						cols.push(col);
						series.push({
							label: `${meta ? meta.title : key} · ${m.label}`,
							stroke: this.colorFor(key),
							dash: m.dash || undefined,
							width: 1.5,
							scale: m.scale,
							spanGaps: false,
							points: { show: false },
						});
					}
				});
			}
			return { data: [xs, ...cols], series };
		},
		render() {
			if(!this.ready) return;
			const { data, series } = this.assemble();
			if(!data[0].length || series.length < 2) {
				if(this.uplot) { this.uplot.destroy(); this.uplot = null; this.sig = null; this._resizeObserver.disconnect(); }
				return;
			}
			const sig = series.map(s => `${s.label || 'x'}|${s.scale || ''}|${s.dash || ''}|${s.stroke || ''}`).join('¦');
			if(!this.uplot || sig !== this.sig) {
				this.buildChart(series, data);
				this.sig = sig;
			} else {
				this.uplot.setData(data);
			}
			this.applyRangeState();
		},
		buildChart(series, data) {
			const el = this.$refs.chart;
			if(!el) return;
			if(this.uplot) { this.uplot.destroy(); this.uplot = null; this._resizeObserver.disconnect(); }
			const zeroFloor = (u, min, max) => [Math.min(0, min || 0), max == null ? 100 : max];
			const opts = {
				width: el.clientWidth || 600,
				height: el.clientHeight || 260,
				series,
				scales: { x: { time: true }, y: { range: zeroFloor }, c: { range: zeroFloor } },
				axes: [
					{ stroke: AXIS_COLOR, grid: { stroke: GRID_COLOR }, ticks: { stroke: GRID_COLOR } },
					{ scale: 'y', stroke: AXIS_COLOR, grid: { stroke: GRID_COLOR }, ticks: { stroke: GRID_COLOR }, values: (u, splits) => splits.map(fmtAxisNum) },
					{ scale: 'c', side: 1, stroke: AXIS_COLOR, grid: { show: false }, ticks: { stroke: GRID_COLOR }, values: (u, splits) => splits.map(fmtAxisNum) },
				],
				legend: { show: false },
				cursor: { drag: { x: true, y: false }, focus: { prox: 20 } },
				plugins: [this.tooltipPlugin()],
				hooks: { setScale: [(u, key) => { if(key === 'x') this._syncRange(u); }] },
			};
			this.uplot = new uPlot(opts, data, el);
			this._resizeObserver.observe(el);
		},

		// --- hover tooltip (replaces the clipped built-in legend) ---
		tooltipPlugin() {
			const self = this;
			let tip;
			return { hooks: {
				init(u) {
					tip = document.createElement('div');
					tip.className = 'stats-tip';
					tip.style.display = 'none';
					u.over.appendChild(tip);
					u.over.addEventListener('mouseleave', () => { tip.style.display = 'none'; });
				},
				setCursor(u) {
					const { idx, left, top } = u.cursor;
					if(idx == null || left < 0) { tip.style.display = 'none'; return; }
					const x = u.data[0][idx];
					let rows = '';
					for(let i = 1; i < u.series.length; i++) {
						const s = u.series[i];
						if(s.show === false) continue;
						const v = u.data[i][idx];
						if(v == null) continue;
						rows += `<div class="r"><span class="d" style="background:${s.stroke}"></span><span class="l">${self.escape(s.label)}</span><b>${self.fmtVal(v)}</b></div>`;
					}
					if(!rows) { tip.style.display = 'none'; return; }
					tip.innerHTML = `<div class="tt">${self.fmtTime(x)}</div>${rows}`;
					tip.style.display = 'block';
					const w = u.over.clientWidth, tw = tip.offsetWidth;
					let lx = left + 14;
					if(lx + tw > w) lx = left - tw - 14;
					if(lx < 0) lx = 2;
					tip.style.transform = `translate(${lx}px, ${Math.max(2, top - 10)}px)`;
				},
			} };
		},
		escape(s) { return String(s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])); },
		fmtVal(v) { return Math.round(v).toLocaleString(); },
		fmtTime(sec) { return new Date(sec * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); },

		// --- zoom / range state ---
		onRangeClick(mins) { this.rangeState = mins == null ? 'full' : mins; this.applyRangeState(); },
		applyRangeState() {
			if(!this.uplot || this.rangeState === 'custom') return;
			const xs = this.uplot.data[0];
			if(!xs || !xs.length) return;
			const dataMin = xs[0], dataMax = xs[xs.length - 1];
			let min, max;
			if(this.rangeState === 'full') { min = dataMin; max = dataMax; }
			else { max = dataMax; min = Math.max(dataMin, dataMax - this.rangeState * 60); }
			this._programmaticScale = true;
			this.uplot.setScale('x', { min, max });
		},
		_syncRange(u) {
			if(this._programmaticScale) { this._programmaticScale = false; return; }
			const xs = u.data[0];
			if(!xs || !xs.length) return;
			const dataMin = xs[0], dataMax = xs[xs.length - 1];
			const { min, max } = u.scales.x;
			const tol = Math.max(2, (dataMax - dataMin) * 0.02);
			if(min <= dataMin + tol && max >= dataMax - tol) { this.rangeState = 'full'; return; }
			let matched = 'custom';
			for(const r of this.ranges) {
				if(r.mins == null) continue;
				if(Math.abs((max - min) - r.mins * 60) <= r.mins * 60 * 0.15 && Math.abs(max - dataMax) <= tol) matched = r.mins;
			}
			this.rangeState = matched;
		},
		resize() {
			const el = this.$refs.chart;
			if(this.uplot && el && el.clientWidth) this.uplot.setSize({ width: el.clientWidth, height: el.clientHeight });
		},
	},
};
</script>

<style scoped>
.stats-controls, .stats-legend {
	flex: 0 0 auto;
	font-size: .82rem;
}
.stats-sep {
	width: 1px;
	align-self: stretch;
	background: rgba(160, 160, 190, 0.3);
	margin: 2px 0;
}
.stats-lg {
	display: inline-flex;
	align-items: center;
	white-space: nowrap;
}
.stats-chip {
	display: inline-flex;
	align-items: center;
	white-space: nowrap;
	background: rgba(160, 160, 190, 0.12);
	border: 1px solid rgba(160, 160, 190, 0.25);
	border-radius: 10px;
	color: inherit;
	font-size: .8rem;
	line-height: 1.2;
	padding: 1px 8px 1px 6px;
	cursor: pointer;
}
.stats-chip:hover { background: rgba(160, 160, 190, 0.22); }
.stats-chip.off { opacity: .4; }
.stats-chip.off .stats-dot { filter: grayscale(1); }
.stats-chip-img {
	width: 14px;
	height: 14px;
	object-fit: cover;
	border-radius: 0; /* keep square, not rounded */
	margin-right: .25rem;
	flex: 0 0 auto;
}
.stats-dot {
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	margin-right: .3rem;
}
/* Colour indicator shown after the channel name in a filter chip. */
.stats-dot-after {
	margin-right: 0;
	margin-left: .35rem;
}
.stats-glyph {
	width: 20px;
	height: 8px;
	margin-right: .3rem;
	overflow: visible;
}
.stats-glyph line {
	stroke: currentColor;
	stroke-width: 2;
}
.stats-chart-wrap {
	min-height: 150px;
}
.stats-chart {
	position: absolute;
	inset: 0;
}
.stats-overlay {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}
</style>

<style>
/* uPlot renders its own (unscoped) DOM, so these are global but namespaced under the panel. */
.stats-tip {
	position: absolute;
	top: 0;
	left: 0;
	pointer-events: none;
	z-index: 10;
	background: rgba(20, 20, 38, 0.95);
	border: 1px solid rgba(160, 160, 190, 0.3);
	border-radius: 4px;
	padding: 5px 7px;
	font-size: 11px;
	line-height: 1.5;
	color: #fff;
	white-space: nowrap;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}
.stats-tip .tt { font-weight: 600; margin-bottom: 2px; opacity: .85; }
.stats-tip .r { display: flex; align-items: center; }
.stats-tip .r .d { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 5px; flex: 0 0 auto; }
.stats-tip .r .l { margin-right: 8px; }
.stats-tip .r b { margin-left: auto; }
</style>
